<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'templates',
    path: '/templates',
    component: 'Templates',
    params: [
      {
        name: 'id',
        prop: 'resourceId',
      },
    ],
    icon: 'fa-file-invoice',
    title: () => pgettext('Templates', 'Templates'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Templates',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    resourceId: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onOptionSelected = (...args) => {
    const [option] = args;
    $router.value.push({
      name: 'app.templates.template',
      params: { id: option.id },
    });
  };

  const formId = ref(null);
  const formName = ref('template');
  const items = ref(null);
  const item = ref(null);
  const editor = ref(null);
  const contextData = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    formName: formName.value,
    items: items.value,
    item: item.value,
    editor: editor.value,
    contextData: contextData.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_items = async () => {
    return $fn.value
      .fetch('list_template')
      .then((d) => _.sortBy(d, 'created_at'));
  };
  const $$load_item = async () => {
    return props.resourceId
      ? $fn.value.fetch('read_template', {
          asResource: true,
          parameters: { id: props.resourceId },
        })
      : null;
  };
  const $$load_editor = async () => {
    return item.value ? item.value.editor() : null;
  };
  const $$load_contextData = async () => {
    return { sampleData: null };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'formName' },
      { name: 'items' },
      { name: 'item' },
      { name: 'editor', dependsOn: ['item'] },
      {
        name: 'contextData',
        cache: { type: 'local', key: 'Template.sampleData' },
      },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      formId: $$load_formId,
      items: $$load_items,
      item: $$load_item,
      editor: $$load_editor,
      contextData: $$load_contextData,
    },
    ux: {},
    dataRefs: {
      formId,
      formName,
      items,
      item,
      editor,
      contextData,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const sampleData = computed(() => {
    if (loaded.value) {
      return contextData.value.sampleData;
    }
    return null;
  });
  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('Templates', 'Templates') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.resourceId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('resourceId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_ha63yv7bo_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_4615685607825208_setData_ahgoj85bu_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'contextData',
      value: { sampleData: null },
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_navigate_v8z0x2umv_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.templates.template',
        params: {
          id: $event.data.option.id,
        },
        replace: true,
      },
    };
  };
  const act_8823104369878821_showModal_urgb220f3_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'TemplatePreview',
      props: {
        item: item.value,
        sampleData: contextData.value.sampleData,
      },
    };
  };
  const act_2237382557075493_showModal_xtr1qr23k_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'FileSelector',
      props: {
        url: $fn.value.urlFor('read_template', { id: 'import' }),
        accept: '.yaml,.yml,.json',
      },
    };
  };
  const act_1040167445267876_setData_cauiopyyz_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: $event.data.response.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_1040167445267876_success_zpvo8cwz0_getWhen = async (
    $event
  ) => {
    return $event.data.response.data;
  };
  const act_1040167445267876_rest_gw9qq02s0_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: {
        self: item.value.links.parent,
        object: 'list',
        data: [],
      },
      resource: editor.value,
    };
  };

  const act_7315092382398562_closeModal_ha63yv7bo =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_ha63yv7bo_getActionArgs,
    });
  const inst_evh_7315092382398562_close_dakdnah32 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_ha63yv7bo,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_dakdnah32],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_48nu3s76t =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_mnbjhbysq =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_48nu3s76t,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_mnbjhbysq],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_v7d01bely =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_78s7ayira =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_v7d01bely,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_78s7ayira],
    event: 'close',
  });
  const act_4615685607825208_setData_ahgoj85bu = new actionTypes.SetDataAction({
    actionArgs: act_4615685607825208_setData_ahgoj85bu_getActionArgs,
  });
  const inst_evh_4615685607825208_optionSelected_hdt9a9vfr =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_setData_ahgoj85bu,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_4615685607825208_navigate_v8z0x2umv =
    new actionTypes.NavigateAction({
      actionArgs: act_4615685607825208_navigate_v8z0x2umv_getActionArgs,
    });
  const inst_evh_4615685607825208_optionSelected_0r7hm0npl =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_navigate_v8z0x2umv,
      event: 'optionSelected',
      displayName: 'navigate',
    });
  const inst_evh_4615685607825208_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_4615685607825208_optionSelected_hdt9a9vfr,
        inst_evh_4615685607825208_optionSelected_0r7hm0npl,
      ],
      event: 'optionSelected',
    });
  const act_2095089031469157_reloadComponentData_m2xez7k91 =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_2095089031469157_clickPreventStop_a67l7rnhx =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_reloadComponentData_m2xez7k91,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_a67l7rnhx],
      event: 'click.prevent.stop',
    });
  const act_8823104369878821_showModal_urgb220f3 =
    new actionTypes.ShowModalAction({
      actionArgs: act_8823104369878821_showModal_urgb220f3_getActionArgs,
    });
  const inst_evh_8823104369878821_clickPreventStop_1afr920s7 =
    new actionTypes.EventHandlerImpl({
      action: act_8823104369878821_showModal_urgb220f3,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_8823104369878821_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8823104369878821_clickPreventStop_1afr920s7],
      event: 'click.prevent.stop',
    });
  const act_2237382557075493_reloadComponentData_55b8qoqy1 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2237382557075493_close_j1704g75h =
    new actionTypes.EventHandlerImpl({
      action: act_2237382557075493_reloadComponentData_55b8qoqy1,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_2237382557075493_showModal_xtr1qr23k =
    new actionTypes.ShowModalAction({
      actionArgs: act_2237382557075493_showModal_xtr1qr23k_getActionArgs,
      events: [inst_evh_2237382557075493_close_j1704g75h],
    });
  const inst_evh_2237382557075493_clickPreventStop_owh0vkvdr =
    new actionTypes.EventHandlerImpl({
      action: act_2237382557075493_showModal_xtr1qr23k,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2237382557075493_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2237382557075493_clickPreventStop_owh0vkvdr],
      event: 'click.prevent.stop',
    });
  const act_1040167445267876_setData_cauiopyyz = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_cauiopyyz_getActionArgs,
  });
  const inst_evh_1040167445267876_success_zpvo8cwz0 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_cauiopyyz,
      event: 'success',
      when: inst_evh_1040167445267876_success_zpvo8cwz0_getWhen,
      displayName: 'setData',
    });
  const act_1040167445267876_rest_gw9qq02s0 = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_gw9qq02s0_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_zpvo8cwz0],
  });
  const inst_evh_1040167445267876_submit_ncqtquch7 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_gw9qq02s0,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_ncqtquch7],
    event: 'submit',
  });

  const actions = {
    inst_evh_7315092382398562_close_dakdnah32,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_mnbjhbysq,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_78s7ayira,
    inst_evh_8564662037462133_close,
    inst_evh_4615685607825208_optionSelected_hdt9a9vfr,
    inst_evh_4615685607825208_optionSelected_0r7hm0npl,
    inst_evh_4615685607825208_optionSelected,
    inst_evh_2095089031469157_clickPreventStop_a67l7rnhx,
    reload: inst_evh_2095089031469157_clickPreventStop_a67l7rnhx,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_8823104369878821_clickPreventStop_1afr920s7,
    inst_evh_8823104369878821_clickPreventStop,
    inst_evh_2237382557075493_close_j1704g75h,
    inst_evh_2237382557075493_clickPreventStop_owh0vkvdr,
    inst_evh_2237382557075493_clickPreventStop,
    inst_evh_1040167445267876_success_zpvo8cwz0,
    inst_evh_1040167445267876_submit_ncqtquch7,
    save: inst_evh_1040167445267876_submit_ncqtquch7,
    inst_evh_1040167445267876_submit,
  };

  const evh_7315092382398562_close_dakdnah32 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_dakdnah32.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_mnbjhbysq = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_mnbjhbysq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_78s7ayira = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_78s7ayira.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_optionSelected_hdt9a9vfr = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_optionSelected_hdt9a9vfr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_optionSelected_0r7hm0npl = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_optionSelected_0r7hm0npl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_optionSelected = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_a67l7rnhx = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_a67l7rnhx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8823104369878821_clickPreventStop_1afr920s7 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8823104369878821_clickPreventStop_1afr920s7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8823104369878821_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8823104369878821_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2237382557075493_close_j1704g75h = async (event, scope) => {
    return await actions.inst_evh_2237382557075493_close_j1704g75h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2237382557075493_clickPreventStop_owh0vkvdr = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2237382557075493_clickPreventStop_owh0vkvdr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2237382557075493_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2237382557075493_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_zpvo8cwz0 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_zpvo8cwz0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_ncqtquch7 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_ncqtquch7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Templates"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <ItpBox :gap="2">
          <UcTemplateSelector
            :resourceId="resourceId"
            @optionSelected="evh_4615685607825208_optionSelected($event, {})"
          >
          </UcTemplateSelector>
          <ItpButton
            variant="default"
            icon="fa-sync-alt"
            :text="gettext('Reload')"
            :spinning="actions.reload.isRunning"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <Vr class="mx-2"> </Vr>
          <ItpButton
            :form="formId"
            :text="pgettext('Templates', 'Save')"
            :icon="$config.saveIcon"
            type="submit"
            variant="primary"
            default
            :spinning="actions.save.isRunning"
          >
          </ItpButton>
          <ItpButton
            :icon="$config.previewIcon"
            :text="pgettext('Templates', 'Preview')"
            variant="light"
            :disabled="!item || !sampleData"
            @click.prevent.stop="
              evh_8823104369878821_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <ItpButton
            :icon="$config.exportIcon"
            :text="pgettext('Templates', 'Export')"
            variant="light"
            :disabled="!item"
            use-auth
            force-download
            downloadFileName="templates.yaml"
            :href="$fn.urlFor('read_template', { id: 'export' })"
          >
          </ItpButton>
          <ItpButton
            :icon="$config.importIcon"
            :text="pgettext('Templates', 'Import')"
            variant="light"
            @click.prevent.stop="
              evh_2237382557075493_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <UcTemplate :item="editor" :context="contextData" v-if="editor">
        </UcTemplate>
      </ItpForm>
    </UcBody>
    <router-view class="d-none"> </router-view>
  </ItpPage>
</template>
